import { useCallback, useMemo } from "react";
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import type {
  WidgetDataPreviewSelection,
  WidgetDataResolveResponse,
} from "@playbookai/types";
import { dashboardApi } from "./api.js";
import { dashboardKeys, type DashboardKeyScope } from "./query-keys.js";

/**
 * Batched widget-data resolution for one page (§10.8/§16.2): one POST per
 * page render cycle, per-slot results, per-slot failure isolation. TanStack's
 * abort signal cancels the request when the page unmounts; `staleTime`
 * matches the datasets' policy default so navigation doesn't re-poll.
 */

export interface UseWidgetDataArgs {
  readonly scope: DashboardKeyScope;
  readonly dashboardId: string | undefined;
  readonly pageId: string | undefined;
  readonly widgetInstanceIds: readonly string[];
  readonly enabled?: boolean;
  readonly preview?: WidgetDataPreviewSelection;
}

export type UseWidgetDataResult = UseQueryResult<WidgetDataResolveResponse> & {
  /** Mark this exact page/instance batch stale and refetch it when active. */
  readonly invalidate: () => Promise<void>;
};

export function useWidgetData(args: UseWidgetDataArgs): UseWidgetDataResult {
  const config = useApiClient();
  const queryClient = useQueryClient();
  const ids = args.widgetInstanceIds;
  const queryKey = useMemo(
    () => [
      ...dashboardKeys.widgetData(
        args.scope,
        args.dashboardId ?? "none",
        args.pageId ?? "none",
        ids,
      ),
      ...(args.preview ? ["preview", args.preview] : []),
    ],
    [args.scope, args.dashboardId, args.pageId, ids, args.preview],
  );
  const query = useQuery<WidgetDataResolveResponse>({
    queryKey,
    enabled:
      args.enabled !== false &&
      args.dashboardId !== undefined &&
      args.pageId !== undefined &&
      ids.length > 0,
    staleTime: 30_000,
    retry: false,
    queryFn: ({ signal }) =>
      dashboardApi.resolveWidgetData(
        config,
        args.dashboardId!,
        {
          pageId: args.pageId!,
          widgetInstanceIds: ids,
          ...(args.preview ? { preview: args.preview } : {}),
        },
        signal,
      ),
  });
  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey, exact: true });
  }, [queryClient, queryKey]);
  return { ...query, invalidate };
}
